/*globals views, components, functionality*/
import React, { useState, useEffect, useContext } from "react";
import styled from "styled-components";
import ActionsContext from "common/ActionsContext";
import Request from "common/Request";
import { safeStr } from "common/api";
import BqnContext from "common/BqnContext";
import { getIndexes } from "common/utils";
import Table from "common/graphs/Table/Table";
import { atoiv6, atoiv4 } from "common/utils";

const createPlaceholder = () =>
  `BQN  ADDRESS         SUBSCRIBER-ID            ACTIVE`;

const ensurePlaceholder = () => (_error) => {
  return { response: createPlaceholder() };
};

function modifyResponse(input, bqnId, nBqnsActive) {
  const [header, ...rows] = input.trim("\n").split("\n");
  const count = {};

  const headerIndex = getIndexes(header);

  for (const row of rows) {
    const attrs = row.trim().split(/\s+/);
    const [address, active] = [
      attrs[headerIndex["ADDRESS"]],
      attrs[headerIndex["ACTIVE"]],
    ];

    if (!count[address]) {
      count[address] = { yes: 0, no: 0 };
    }

    count[address][active] += 1;
  }

  const included = new Set();
  const output =
    `${header}\n` +
    rows
      .filter((line) => {
        const attrs = line.trim().split(/\s+/);
        const [address, active] = [
          attrs[headerIndex["ADDRESS"]],
          attrs[headerIndex["ACTIVE"]],
        ];
        if (bqnId === "all") {
          if (!included.has(`${address}${active}`)) {
            included.add(`${address}${active}`);
            return true;
          } else {
            return count[address][active] < nBqnsActive;
          }
        } else {
          return attrs[headerIndex["BQN"]] === bqnId.toString();
        }
      })
      .map((line) => {
        const attrs = line.trim().split(/\s+/);
        const bqn = attrs[headerIndex["BQN"]];
        const address = attrs[headerIndex["ADDRESS"]];
        const subscriberId = attrs[headerIndex["SUBSCRIBER-ID"]];
        const active = attrs[headerIndex["ACTIVE"]];
        const bqnDisplayed =
          bqnId === "all" && count[address][active] === nBqnsActive
            ? "All"
            : bqn;
        return `${bqnDisplayed} ${address} ${subscriberId} ${active}`;
      })
      .join("\n");

  return output;
}

const doIpAddressesRequest = (subscriberGroup, bqnId, nBqnsActive) =>
  ifCl
    .run(`show api subscriber-group name ${safeStr(subscriberGroup)} active`)
    .then((response) => modifyResponse(response, bqnId, nBqnsActive))
    .then((response) => ({ response }))
    .catch(ensurePlaceholder());

const ResultsLayout = styled.div`
  margin-left: 5px;
  flex: 100%;
  overflow: hidden; /*list will scroll*/
`;

const TableContainer = styled.div`
  width: 100%;
  overflow: auto;
  --is-highest: #3d85c6;
  --is-higher: #6aa84f;
  --is-lower: #e69138;
  --is-lowest: #cc0000;
  --warning: #cc0000;
  .inverted {
    --is-lowest: #3d85c6;
    --is-lower: #6aa84f;
    --is-higher: #e69138;
    --is-highest: #cc0000;
  }
  .dataTable tbody td {
    transition: color 1s, background-color: 1s;
  }
  &.colorize-warnings .dataTable tbody td {
    &.warning {
      color: white;
      background: var(--warning);
    }
  }
  table.MuiTable-root {
    min-width: auto !important;
    max-width: auto !important;
  }
`;

const getColumns = (mode, openSubscriberDetails) => {
  return [
    ...(mode === "cluster"
      ? [
        {
          label: "BQN",
          id: "BQN",
          title: "BQN",
          numeric: false, // alignment reasons
        },
      ]
      : []),
    {
      label: "ADDRESS",
      id: "ADDRESS",
      numeric: false,
      title: "ADDRESS",
      cellClassName: "hyperlink-text grow-width",
      onClick: (addr) => {
        openSubscriberDetails({ addr });
      },
      parse: (value) => !value ? 0 :
        value.includes(":") ? atoiv6(value) : atoiv4(value),
      parseFilter: (value) => value
    },
    {
      label: "ACTIVE",
      id: "ACTIVE",
      numeric: false,
      title: "ACTIVE",
    },
  ];
};

const ActiveIpAddressesDatatable = ({ request }) => {
  const clusterStatus = useContext(BqnContext).clusterStatus;
  const mode = clusterStatus?.mode;

  const openSubscriberDetails = (target) => {
    const returnTo = "viewStatusSubscriberGroupsEdit";
    globalNavigate("viewSubsDetail", { returnView: returnTo, ...target });
  };

  return (
    <TableContainer className={"colorize-warnings"}>
      <Request during={request}>
        {({ response }) => {
          const [header, ...rows] = response.trim().split("\n");
          const headerAttrs = header.trim().split(/\s+/);
          const columns = getColumns(mode, openSubscriberDetails);
          const newRows = rows.map((row) => {
            const rowAttrs = row.trim(/\s/).split(/\s+/);
            const newEntry = rowAttrs.reduce((acc, attr, index) => {
              acc[headerAttrs[index]] = attr;
              return acc;
            }, {});
            return newEntry;
          });

          return (
            <Table
              isSelectable={false}
              isRowSelectable={false}
              data={newRows}
              sort={true}
              columns={columns}
              search={true}
              csvPrefix="current-ip-addresses"
              rowsPerPageNr={50}
            />
          );
        }}
      </Request>
    </TableContainer>
  );
};

export const ActiveIpAddressesTable = ({ subscriberGroup }) => {
  const [request, setRequest] = useState(null);
  const actions = useContext(ActionsContext);
  const { bqnId, bqns } = useContext(BqnContext);
  const nBqnsActive = bqns.filter((bqn) => bqn.function !== "offline").length;

  const doLoad = () =>
    setRequest(doIpAddressesRequest(subscriberGroup, bqnId, nBqnsActive));

  useEffect(() => {
    doLoad();
    return actions.recv("do-load-subscriber-groups-edit", doLoad);
  }, [bqnId]);

  return request === null ? null : (
    <ResultsLayout>
      <ActiveIpAddressesDatatable request={request} />
    </ResultsLayout>
  );
};
